import { TopicWithConfig } from 'widgets/myPortfolioCapsules/topics/Topic';
import { TopicType } from 'widgets/myPortfolioCapsules/topics/TopicType';
import {
    MyPortfolioCapsulesDataStateAggregation,
    MyPortfolioCapsulesSymbolDataState,
    MyPortfolioCapsulesSymbolDataWeights,
} from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesDataState';
import {
    PortfolioItem,
    portfolioItemOrderValues,
} from 'portfolio/PortfolioItem';
import { MyPortfolioCapsulesWidgetConfigState } from 'widgets/myPortfolioCapsules/store/config/MyPortfolioCapsulesWidgetConfigState';
import { MyPortfolioCapsulesDataItem } from 'widgets/myPortfolioCapsules/store/data/MyPortfolioCapsulesData';
import React from 'react';
import { ValueTopicConfig } from 'widgets/myPortfolioCapsules/topics/value/valueTopicConfig';
import { ValueTopicToolbar } from 'widgets/myPortfolioCapsules/topics/value/valueTopicToolbar';
import { IndigoAppContextType } from 'app/AppContext';

export const valueTopic: TopicWithConfig<ValueTopicConfig> = {
    type: TopicType.Value,
    description: 'Value',
    requiredPortfolioColumns: ['qty', 'previousNightClose', 'averagePrice'],
    accumulatedColumns: [],
    weights: [],
    calculateDerivedValues,
    getSymbolValues,
    getInitialTopicConfig: () => ({ quantities: 'ordered' }),
    help: (
        <div>
            This topic ranks symbols in your portfolio on their notional value.
            You can choose to use the order quantity, traded quantity or
            remaining quantity in the calculation. You can sort by ascending or
            descending and configure the number of symbols you would like to
            display.
        </div>
    ),
    toolbarItems: <ValueTopicToolbar />,
    getDescription: getLegendLabel,
    deriveShouldExcludeZeros: () => true,
};

export function calculateDerivedValues(
    item: PortfolioItem,
    symbolDataState: MyPortfolioCapsulesSymbolDataState,
    itemWeights: MyPortfolioCapsulesSymbolDataWeights,
    factor: 1 | -1
) {
    const { tradedValue, remainingValue, orderedValue } =
        portfolioItemOrderValues(item, factor);

    symbolDataState.derived.valueByTradedQty =
        (symbolDataState.derived.valueByTradedQty ?? 0) + tradedValue;
    symbolDataState.derived.valueByRemainingQty =
        (symbolDataState.derived.valueByRemainingQty ?? 0) + remainingValue;
    symbolDataState.derived.valueByOrderQty =
        (symbolDataState.derived.valueByOrderQty ?? 0) + orderedValue;
}

/*
 * Given aggregated data, calculates the values per symbol
 */
function getSymbolValues(
    aggregatedData: MyPortfolioCapsulesDataStateAggregation,
    config: MyPortfolioCapsulesWidgetConfigState & ValueTopicConfig,
    exchangeRate: number
): MyPortfolioCapsulesDataItem[] {
    return Object.keys(aggregatedData.bySymbol)
        .map((symbol) => ({
            symbol,
            symbolData: aggregatedData.bySymbol[symbol],
        }))
        .map((data) => {
            const { symbol, symbolData } = data;

            const {
                derived: {
                    valueByOrderQty,
                    valueByTradedQty,
                    valueByRemainingQty,
                },
            } = symbolData;

            const value =
                (config.quantities === 'ordered'
                    ? valueByOrderQty
                    : config.quantities === 'traded'
                    ? valueByTradedQty
                    : valueByRemainingQty) ?? 0;

            const usersValue = value * exchangeRate;

            return {
                symbol,
                value: usersValue,
                qty: symbolData.accumulated.qty,
                qtyTraded: symbolData.accumulated.qtyTraded,
                qtyRemaining: symbolData.accumulated.qtyRemaining,
            };
        });
}

function getLegendLabel(
    appContext: IndigoAppContextType,
    config: MyPortfolioCapsulesWidgetConfigState & ValueTopicConfig
) {
    return `${
        config.quantities === 'traded'
            ? 'Traded'
            : config.quantities === 'leaves'
            ? 'Leaves'
            : 'Ordered'
    } Value`;
}
